import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from 'lucide-react';

import { api, type PersonaDetail, type PersonaPublishPreview, type PlanEntry } from '@/lib/api';
import { useContent, useLabel, useT, useTn } from '@/lib/i18n';
import { InfoHint } from '@/components/info-hint';
import { notify } from '@/lib/notify';
import { useCannotBecause } from '@/lib/permissions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * The persona editor — identity, plan template, publish (`plan-beta` C, `11` Flow 5).
 *
 * WHY THIS SCREEN HAS A "SAVE DRAFT" AND A SEPARATE "PUBLISH", WHICH NO OTHER
 * EDITOR IN THIS CONSOLE HAS.
 *
 * A persona's plan is the only catalogue field that candidates are *pinned to*.
 * `generation.ts` stamps `planVersion` onto every instance, and G4's candidate
 * contexts will pin it for the days a candidate spends sitting three modules. An
 * ordinary save would therefore rewrite what a completed assessment was
 * administered under, retroactively and unrecoverably — `plan-beta` §5 calls
 * that the single most damaging thing available in this codebase.
 *
 * So editing writes a draft nothing resolves against, and publishing is the act
 * that mints a version and supersedes (never overwrites) the one in force. The
 * two are separate buttons because they are separate decisions.
 *
 * PUBLISH IS GATED ON A PREVIEW, and re-gated whenever the draft changes after
 * one — the same rule the module config editor follows, for the same reason: the
 * preview is where an amber cell is caught before a candidate meets it, and a
 * preview an operator can skip is a preview that does not exist.
 *
 * Identity fields (name, segment, locale) save immediately. They are labels;
 * nothing pins them.
 */

const LOCALES = ['ar-AE', 'en-AE', 'en-GB'];

const CHANGE_TONE: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  added: 'default',
  changed: 'secondary',
  removed: 'destructive',
  unchanged: 'outline',
};

function sameplan(a: PlanEntry[], b: PlanEntry[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/* --------------------------------------------------------------- the plan */

function PlanRows({
  plan,
  moduleIds,
  onChange,
}: {
  plan: PlanEntry[];
  moduleIds: string[];
  onChange: (next: PlanEntry[]) => void;
}) {
  const t = useT();

  /** Reorder by index, then renumber — `order` is the stored fact, not the array position. */
  const move = (index: number, by: number) => {
    const next = [...plan];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next.map((entry, i) => ({ ...entry, order: i + 1 })));
  };

  const set = (index: number, patch: Partial<PlanEntry>) =>
    onChange(plan.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));

  if (plan.length === 0) {
    return (
      <p className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
        {t('personaEditor.emptyPlan')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {plan.map((entry, index) => (
        <div key={`${entry.moduleId}-${index}`} className="grid grid-cols-12 items-center gap-2 rounded-md border p-2">
          <div className="col-span-5">
            <Select value={entry.moduleId} onValueChange={(moduleId) => set(index, { moduleId })}>
              <SelectTrigger aria-label={t('personaEditor.moduleAt', { n: index + 1 })}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {moduleIds.map((id) => (
                  <SelectItem key={id} value={id}>{id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-3">
            <Input
              type="number"
              min={1}
              max={60}
              value={entry.itemCount}
              aria-label={t('personaEditor.itemCountFor', { module: entry.moduleId })}
              onChange={(e) => set(index, { itemCount: Number(e.target.value) })}
            />
          </div>

          <label className="col-span-2 flex items-center gap-2 text-sm">
            <Checkbox
              checked={entry.required}
              aria-label={t('personaEditor.requiredFor', { module: entry.moduleId })}
              onCheckedChange={(checked) => set(index, { required: checked === true })}
            />
            {t('personaEditor.required')}
          </label>

          <div className="col-span-2 flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('personaEditor.moveUp', { module: entry.moduleId })}
              onClick={() => move(index, -1)}
            >
              <ArrowUpIcon className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('personaEditor.moveDown', { module: entry.moduleId })}
              onClick={() => move(index, 1)}
            >
              <ArrowDownIcon className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('personaEditor.remove', { module: entry.moduleId })}
              onClick={() => onChange(plan.filter((_, i) => i !== index).map((e, i) => ({ ...e, order: i + 1 })))}
            >
              <TrashIcon className="size-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- the preview */

function PublishPreview({ preview }: { preview: PersonaPublishPreview }) {
  const t = useT();
  const label = useLabel();
  const changes = preview.diff.filter((d) => d.change !== 'unchanged');

  return (
    <div className="space-y-3 text-sm">
      <p className="text-muted-foreground">
        {preview.nextPlanVersion
          ? t('personaEditor.willMint', {
              next: preview.nextPlanVersion,
              current: preview.currentPlanVersion,
            })
          : t('personaEditor.nothingToPublish', { current: preview.currentPlanVersion })}
      </p>

      {/* Reported, never blocking. An instance froze its items, so a run already
          generated completes under the version it pinned whatever happens here —
          and an operator seeing a count with no explanation assumes otherwise. */}
      {(preview.pinnedRuns.open > 0 || preview.pinnedRuns.completed > 0) && (
        <p className="text-muted-foreground">
          {t('personaEditor.pinnedRuns', {
            open: preview.pinnedRuns.open,
            completed: preview.pinnedRuns.completed,
            version: preview.currentPlanVersion,
          })}
        </p>
      )}

      {preview.blockers.map((b) => (
        <Alert key={b.code + b.message} variant="destructive">
          <AlertDescription>
            <span className="font-mono text-xs" dir="ltr">{b.code}</span> — {b.message}
          </AlertDescription>
        </Alert>
      ))}

      {preview.warnings.map((w) => (
        <Alert key={w.code + w.message}>
          <AlertDescription>
            <span className="font-mono text-xs" dir="ltr">{w.code}</span> — {w.message}
          </AlertDescription>
        </Alert>
      ))}

      {changes.length > 0 && (
        <div>
          <h4 className="mb-1 font-medium">{t('personaEditor.whatChanges')}</h4>
          <ul className="space-y-1">
            {changes.map((d) => (
              <li key={d.moduleId} className="flex items-center gap-2">
                <Badge variant={CHANGE_TONE[d.change]}>
                  {label('personaEditor.change', d.change)}
                </Badge>
                <span className="font-mono text-xs">{d.moduleId}</span>
                {d.detail.length > 0 && (
                  <span className="text-muted-foreground text-xs">{d.detail.join(' · ')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h4 className="mb-1 font-medium">{t('personaEditor.coverage')}</h4>
        <ul className="space-y-1">
          {preview.coverage.map((c) => (
            <li key={c.moduleId} className="flex items-center gap-2">
              <Badge variant={c.status === 'ok' ? 'secondary' : 'destructive'}>
                {c.status === 'no_cell'
                  ? t('matrix.noCell')
                  : t('matrix.available', { count: c.effectivePoolSize })}
              </Badge>
              <span className="font-mono text-xs" dir="ltr">{c.moduleId}</span>
              <span className="text-muted-foreground text-xs">
                {t('personaEditor.asksFor', { count: c.itemCount })}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- the editor */

export function PersonaEditor({ personaId, onClose }: { personaId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const t = useT();
  const tn = useTn();
  const content = useContent();

  /**
   * R3, and the two capabilities are the whole shape of this screen.
   *
   * Details, the plan draft, preview and discard are `design:draft` — reversible
   * work against something nothing resolves against. **Publish is
   * `catalogue:publish`**, because it mints the `planVersion` a live candidate
   * is generated from and cannot be un-decided by writing another draft.
   */
  const cannotDraft = useCannotBecause('design:draft');
  const cannotPublish = useCannotBecause('catalogue:publish');

  const persona = useQuery({ queryKey: ['persona', personaId], queryFn: () => api.persona(personaId) });
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });

  const [details, setDetails] = useState<{ nameEn: string; nameAr: string; segment: string; locale: string } | null>(null);
  const [plan, setPlan] = useState<PlanEntry[] | null>(null);
  const [preview, setPreview] = useState<PersonaPublishPreview | null>(null);

  const loaded: PersonaDetail | undefined = persona.data;

  // Seed the local form once, from the server's state. `plan` starts at the
  // DRAFT when one is pending and the published plan otherwise — which is
  // exactly what the server would work against, so the two never disagree about
  // what is being edited.
  useEffect(() => {
    if (!loaded || details !== null) return;
    setDetails({
      nameEn: loaded.name.en,
      nameAr: loaded.name.ar,
      segment: loaded.segment,
      locale: loaded.defaultLocale,
    });
    setPlan(loaded.draftPlanTemplate ?? loaded.planTemplate);
  }, [loaded, details]);

  const moduleIds = useMemo(
    () => (modules.data?.items ?? []).map((m) => m.moduleId).sort(),
    [modules.data],
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['persona', personaId] });
    void queryClient.invalidateQueries({ queryKey: ['personas'] });
    void queryClient.invalidateQueries({ queryKey: ['matrix'] });
    void queryClient.invalidateQueries({ queryKey: ['coverage'] });
  };

  const saveDetails = useMutation({
    mutationFn: () =>
      api.updatePersona(personaId, {
        name: { en: details!.nameEn, ar: details!.nameAr },
        segment: details!.segment,
        defaultLocale: details!.locale,
      }),
    onSuccess: () => {
      notify.success(t('personaEditor.detailsSaved'), t('personaEditor.detailsSaved.detail'));
      refresh();
    },
    onError: (err) =>
      notify.error(t('personaEditor.saveFailed'), err instanceof Error ? err.message : undefined),
  });

  const saveDraft = useMutation({
    mutationFn: () => api.updatePersona(personaId, { draftPlanTemplate: plan! }),
    onSuccess: () => {
      // Gate the publish again: the preview that was on screen described a
      // different plan, and a stale one is worse than none because it is
      // trusted.
      setPreview(null);
      notify.success(t('personaEditor.draftSaved'), t('personaEditor.draftSaved.detail'));
      refresh();
    },
    onError: (err) =>
      notify.error(t('personaEditor.draftSaveFailed'), err instanceof Error ? err.message : undefined),
  });

  const runPreview = useMutation({
    mutationFn: () => api.previewPersona(personaId),
    onSuccess: setPreview,
    onError: (err) =>
      notify.error(t('personaEditor.previewFailed'), err instanceof Error ? err.message : undefined),
  });

  const publish = useMutation({
    mutationFn: () => api.publishPersona(personaId),
    onSuccess: ({ persona: saved }) => {
      notify.success(
        t('personaEditor.published', { version: saved.planVersion }),
        `${tn('modules.count', saved.planTemplate.length)}. ${t('personaEditor.published.detail')}`,
      );
      setPreview(null);
      setPlan(saved.planTemplate);
      refresh();
    },
    onError: (err) =>
      notify.error(t('personaEditor.publishFailed'), err instanceof Error ? err.message : undefined),
  });

  const discard = useMutation({
    mutationFn: () => api.discardPersonaDraft(personaId),
    onSuccess: (saved) => {
      setPlan(saved.planTemplate);
      setPreview(null);
      notify.success(
        t('personaEditor.discarded'),
        t('personaEditor.discarded.detail', { version: saved.planVersion }),
      );
      refresh();
    },
    onError: (err) =>
      notify.error(t('personaEditor.discardFailed'), err instanceof Error ? err.message : undefined),
  });

  if (persona.isLoading || !loaded || !details || plan === null) {
    return (
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader><DialogTitle>{t('personaEditor.loading')}</DialogTitle></DialogHeader>
        <Skeleton className="h-64 w-full" />
      </DialogContent>
    );
  }

  const storedPlan = loaded.draftPlanTemplate ?? loaded.planTemplate;
  const dirty = !sameplan(plan, storedPlan);
  const busy = saveDraft.isPending || publish.isPending || discard.isPending || runPreview.isPending;

  return (
    <DialogContent className="max-h-[90vh] sm:max-w-3xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {content(loaded.name)}
          <Badge variant={loaded.active ? 'default' : 'outline'}>
            {t(loaded.active ? 'personas.active' : 'personas.retiredState')}
          </Badge>
          <Badge variant="secondary" className="font-mono" dir="ltr">{loaded.planVersion}</Badge>
          {loaded.hasDraft && (
            <Badge variant="destructive">{t('personaEditor.unpublishedDraft')}</Badge>
          )}
        </DialogTitle>
        <DialogDescription>{t('personaEditor.description')}</DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        {/* ------------------------------------------------------ identity */}
        <section className="space-y-3">
          <h3 className="text-sm font-medium">{t('createModule.step1')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="persona-name-en">{t('createModule.name.en')}</Label>
              <Input
                id="persona-name-en"
                dir="ltr"
                value={details.nameEn}
                onChange={(e) => setDetails({ ...details, nameEn: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="persona-name-ar">{t('createModule.name.ar')}</Label>
              <Input
                id="persona-name-ar"
                dir="rtl"
                value={details.nameAr}
                onChange={(e) => setDetails({ ...details, nameAr: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="persona-segment">{t('personaEditor.segment')}</Label>
              <Input
                id="persona-segment"
                value={details.segment}
                onChange={(e) => setDetails({ ...details, segment: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="persona-locale">{t('personaEditor.defaultLocale')}</Label>
              <Select value={details.locale} onValueChange={(locale) => setDetails({ ...details, locale })}>
                <SelectTrigger id="persona-locale"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCALES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => saveDetails.mutate()}
            disabled={saveDetails.isPending || cannotDraft !== null} title={cannotDraft ?? undefined}>
            {saveDetails.isPending ? t('common.saving') : t('personaEditor.saveDetails')}
          </Button>
        </section>

        {/* ---------------------------------------------------- plan rows */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">
              {t('personaEditor.planTemplate')}
              <InfoHint>{t('personaEditor.itemCountNote')}</InfoHint>
              <span className="text-muted-foreground ms-2 text-xs font-normal">
                {loaded.hasDraft
                  ? t('personaEditor.editingDraft')
                  : t('personaEditor.editingCopy', { version: loaded.planVersion })}
              </span>
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setPlan([
                  ...plan,
                  { moduleId: moduleIds[0] ?? '', order: plan.length + 1, required: true, itemCount: 10 },
                ])
              }
              disabled={moduleIds.length === 0}
            >
              {t('personaEditor.addModule')}
            </Button>
          </div>

          <PlanRows plan={plan} moduleIds={moduleIds} onChange={(next) => { setPlan(next); setPreview(null); }} />



          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => saveDraft.mutate()}
              disabled={!dirty || busy || cannotDraft !== null} title={cannotDraft ?? undefined}>
              {saveDraft.isPending ? t('common.saving') : t('personaEditor.saveDraft')}
            </Button>
            {loaded.hasDraft && (
              <Button variant="outline" size="sm" onClick={() => discard.mutate()}
                disabled={busy || cannotDraft !== null} title={cannotDraft ?? undefined}>
                {t('personaEditor.discardDraft')}
              </Button>
            )}
          </div>
        </section>

        {/* ------------------------------------------------------ publish */}
        <section className="space-y-3">
          <h3 className="text-sm font-medium">{t('personaEditor.publish')}</h3>

          {dirty && (
            <Alert>
              <AlertDescription>{t('personaEditor.unsaved')}</AlertDescription>
            </Alert>
          )}

          <Button variant="outline" size="sm" onClick={() => runPreview.mutate()}
            disabled={busy || cannotDraft !== null} title={cannotDraft ?? undefined}>
            {runPreview.isPending ? t('config.checking') : t('personaEditor.previewAction')}
          </Button>

          {preview && <PublishPreview preview={preview} />}
        </section>

        {/* ------------------------------------------------------ history */}
        {loaded.planTemplateHistory.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium">{t('personaEditor.superseded')}</h3>
            <p className="text-muted-foreground text-xs">{t('personaEditor.superseded.hint')}</p>
            <ul className="space-y-1 text-sm" aria-label={t('personaEditor.superseded')}>
              {loaded.planTemplateHistory.map((v) => (
                <li key={v.planVersion} className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono" dir="ltr">{v.planVersion}</Badge>
                  <span className="text-muted-foreground text-xs">
                    {v.planTemplate.map((m) => m.moduleId).join(' → ') || t('personaEditor.noModules')}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
        <Button
          onClick={() => publish.mutate()}
          title={cannotPublish ?? undefined}
          disabled={
            // R3. Publishing mints a planVersion a live candidate is generated
            // from — `catalogue:publish`, admin only. Everything else on this
            // screen writes a DRAFT that nothing resolves against.
            cannotPublish !== null
            || busy
            || dirty
            || !loaded.hasDraft
            // Gated on the preview, and on the preview having nothing to refuse.
            // Publishing past a blocker would 422 anyway; the difference is
            // whether the operator finds out before or after they commit to it.
            || preview === null
            || preview.blockers.length > 0
          }
        >
          {publish.isPending ? t('personaEditor.publishing') : t('personaEditor.publishPlan')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* -------------------------------------------------------------- create */

const PERSONA_ID = /^[a-z][a-z0-9_.]{3,63}$/;

/**
 * A new persona is created UNPUBLISHED, with an empty plan in force.
 *
 * It could have arrived with its plan already live, and that would be worse in a
 * specific way: a plan is only meaningful against the matrix cells and the
 * certified bank behind it, and neither exists at the moment of creation.
 * Publishing at create would mint a version for a plan whose coverage has never
 * been checked — the thing the draft exists to make impossible.
 */
export function CreatePersonaDialog({ onClose }: { onClose: (personaId?: string) => void }) {
  const queryClient = useQueryClient();
  // Creating a persona starts it unpublished, administering nothing — a draft.
  const cannotDraft = useCannotBecause('design:draft');
  const t = useT();
  const [form, setForm] = useState({ personaId: '', nameEn: '', nameAr: '', segment: '', locale: 'ar-AE' });

  const create = useMutation({
    mutationFn: () =>
      api.createPersona({
        personaId: form.personaId,
        name: { en: form.nameEn, ar: form.nameAr },
        segment: form.segment,
        defaultLocale: form.locale,
        active: true,
      }),
    onSuccess: (persona) => {
      void queryClient.invalidateQueries({ queryKey: ['personas'] });
      notify.success(
        t('personaEditor.created', { persona: persona.personaId }),
        t('personaEditor.created.detail'),
      );
      onClose(persona.personaId);
    },
    onError: (err) =>
      notify.error(t('personaEditor.createFailed'), err instanceof Error ? err.message : undefined),
  });

  const idValid = PERSONA_ID.test(form.personaId);
  const complete = idValid && form.nameEn.trim() && form.nameAr.trim() && form.segment.trim();

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{t('personas.new')}</DialogTitle>
        <DialogDescription>{t('personaEditor.create.description')}</DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <div>
          <Label htmlFor="new-persona-id">{t('personaEditor.personaId')}</Label>
          <Input
            id="new-persona-id"
            placeholder="persona.hidden_graduate"
            className="font-mono"
            dir="ltr"
            value={form.personaId}
            onChange={(e) => setForm({ ...form, personaId: e.target.value })}
          />
          {form.personaId !== '' && !idValid && (
            <p className="text-destructive mt-1 text-xs">{t('personaEditor.personaId.invalid')}</p>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="new-persona-name-en">{t('createModule.name.en')}</Label>
            <Input
              id="new-persona-name-en"
              dir="ltr"
              value={form.nameEn}
              onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="new-persona-name-ar">{t('createModule.name.ar')}</Label>
            <Input
              id="new-persona-name-ar"
              dir="rtl"
              value={form.nameAr}
              onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
            />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="new-persona-segment">{t('personaEditor.segment')}</Label>
            <Input
              id="new-persona-segment"
              placeholder="Graduate · 21–27"
              value={form.segment}
              onChange={(e) => setForm({ ...form, segment: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="new-persona-locale">{t('personaEditor.defaultLocale')}</Label>
            <Select value={form.locale} onValueChange={(locale) => setForm({ ...form, locale })}>
              <SelectTrigger id="new-persona-locale"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOCALES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onClose()}>{t('common.cancel')}</Button>
        <Button onClick={() => create.mutate()}
          disabled={!complete || create.isPending || cannotDraft !== null}
          title={cannotDraft ?? undefined}>
          {create.isPending ? t('users.creating') : t('personaEditor.createPersona')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
